import { hrmsGet } from "../api/api";
import { fetchHoliday } from "./holidayService";

const HOLIDAY_CATEGORY_KEYWORDS = [
  "public holiday",
  "national holiday",
  "festival holiday",
  "company holiday",
  "weekly off",
  "non-working",
  "non working",
  "holiday",
];

function unwrapList(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data?.content)) return response.data.content;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.content)) return response.content;
  return [];
}

function toDateKey(value) {
  if (!value) return null;

  if (value instanceof Date) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    return raw.slice(0, 10);
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return toDateKey(parsed);
}

function enumerateDateKeys(startValue, endValue) {
  const startKey = toDateKey(startValue);
  const endKey = toDateKey(endValue);
  if (!startKey || !endKey) return [];

  const [startYear, startMonth, startDay] = startKey.split("-").map(Number);
  const [endYear, endMonth, endDay] = endKey.split("-").map(Number);
  const start = new Date(startYear, startMonth - 1, startDay);
  const end = new Date(endYear, endMonth - 1, endDay);

  if (end < start) return [];

  const keys = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    keys.push(toDateKey(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return keys;
}

function normalizeCategoryName(category) {
  return String(category?.categoryName || category?.name || "").trim().toLowerCase();
}

export function isHolidayCategory(category) {
  if (!category) return false;
  if (category.isHoliday === true || category.holiday === true) return true;

  const name = normalizeCategoryName(category);
  if (!name) return false;

  return HOLIDAY_CATEGORY_KEYWORDS.some((keyword) => name.includes(keyword));
}

function resolveCategoryName(event, categoriesById) {
  const categoryId = event?.categoryId ?? event?.category?.categoryId;
  const matched = categoriesById.get(Number(categoryId));
  return (
    event?.categoryName ||
    event?.category?.categoryName ||
    matched?.categoryName ||
    matched?.name ||
    ""
  );
}

export function isHolidayEvent(event, categories = []) {
  if (!event || event.isEnabled === false) return false;

  const categoriesById = new Map(
    categories.map((category) => [Number(category.categoryId ?? category.id), category])
  );

  const categoryId = Number(event?.categoryId ?? event?.category?.categoryId);
  const matchedCategory = categoriesById.get(categoryId);

  if (matchedCategory && isHolidayCategory(matchedCategory)) {
    return true;
  }

  if (event?.isHoliday === true || event?.holiday === true) {
    return true;
  }

  const categoryName = resolveCategoryName(event, categoriesById).toLowerCase();
  if (categoryName && HOLIDAY_CATEGORY_KEYWORDS.some((keyword) => categoryName.includes(keyword))) {
    return true;
  }

  // Legacy mapping used by holiday management screens.
  if (categoryId === 1) {
    return true;
  }

  return false;
}

export function buildHolidayDateSet(events = [], categories = []) {
  const holidayDates = new Set();

  for (const event of events) {
    if (!isHolidayEvent(event, categories)) continue;

    const start = event.startTime || event.startDate || event.start;
    const end = event.endTime || event.endDate || event.end || start;

    for (const dateKey of enumerateDateKeys(start, end)) {
      holidayDates.add(dateKey);
    }
  }

  return holidayDates;
}

/**
 * GET /org-calendar-events/search?page=0&size=10&sortBy=startTime&sortDir=ASC
 * X-Service-Code: HR14U
 */
export async function fetchCalendarEvents({
  page = 0,
  size = 100,
  sortBy = "startTime",
  sortDir = "ASC",
} = {}) {
  const result = await hrmsGet(
    `/org-calendar-events/search?page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}`,
    "HR14U"
  );
  return unwrapList(result);
}

export async function fetchCalendarCategories() {
  const response = await fetchHoliday();
  return Array.isArray(response) ? response : response?.data ?? [];
}

export async function fetchAllCalendarEvents({ pageSize = 100 } = {}) {
  let page = 0;
  let allEvents = [];
  let hasMore = true;

  while (hasMore) {
    const result = await hrmsGet(
      `/org-calendar-events/search?page=${page}&size=${pageSize}&sortBy=startTime&sortDir=ASC`,
      "HR14U"
    );
    const content = unwrapList(result);
    allEvents = allEvents.concat(content);

    const isLast = result?.data?.last ?? result?.last ?? content.length < pageSize;
    hasMore = !isLast && content.length > 0;
    page += 1;
  }

  return allEvents;
}

export function getHolidayValidationMessage(date) {
  // return `The selected date (${format(date, "dd-MMM-yyyy")}) is a holiday.`;
}

/**
 * Validates leave start/end dates against organization holidays.
 * Fetches calendar events before validation as required by the apply-leave flow.
 */
export async function validateLeaveDatesForHolidays(fromDate, toDate) {
  if (!fromDate || !toDate) {
    return { valid: true };
  }

  try {
    const [events, categories] = await Promise.all([
      fetchAllCalendarEvents(),
      fetchCalendarCategories(),
    ]);

    const holidayDates = buildHolidayDateSet(events, categories);
    const startKey = toDateKey(fromDate);
    const endKey = toDateKey(toDate);

    if (startKey && holidayDates.has(startKey)) {
      return {
        valid: false,
        message: getHolidayValidationMessage(fromDate),
      };
    }

    if (endKey && endKey !== startKey && holidayDates.has(endKey)) {
      return {
        valid: false,
        message: getHolidayValidationMessage(toDate),
      };
    }

    return { valid: true };
  } catch (error) {
    console.error("Failed to validate leave dates against holidays", error);
    return {
      valid: false,
      message: "Unable to verify holidays at this time. Please try again later.",
      error,
    };
  }
}
